// Bảng tin: cursor pages over a ranked snapshot (BA v1.1), ~2 pages fetched ahead,
// next-page thumbs pre-decoded off-thread, first page cached for an instant paint.

import { h } from '../dom';
import { api, resolveApiUrl, type FeedPage, type OnThisDay, type Post, type Streak, type User } from '../../api/client';
import { avatar, empty, photoImg, skeleton } from '../components';
import { postCard } from '../post';
import { setCounts, shell } from '../shell';
import { pushCard } from '../pushui';
import { inviteToShoot } from './profile';

const PAGE = 10;
const AHEAD = 2; // pages kept loaded beyond the viewport
const CACHE_KEY = 'booth.feed.v1';

interface Cached { userId: string; at: number; items: Post[] }
const cache = {
  get(userId: string): Cached | null {
    try {
      const c = JSON.parse(localStorage.getItem(CACHE_KEY) ?? 'null') as Cached | null;
      return c && c.userId === userId && Date.now() - c.at < 7 * 86_400_000 ? c : null;
    } catch { return null; }
  },
  set(userId: string, items: Post[]) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ userId, at: Date.now(), items } satisfies Cached)); } catch { /* quota */ }
  },
};

// ------------------------------------------------------------------ image pre-decoding

const decoded = new Set<string>();
const queue: string[] = [];
let active = 0;
const MAX_DECODES = 4;

/** Warms the HTTP + decode cache for upcoming thumbs (at most 4 in flight). */
function predecode(urls: string[]) {
  for (const u of urls) if (!decoded.has(u)) { decoded.add(u); queue.push(u); }
  pump();
}
function pump() {
  while (active < MAX_DECODES && queue.length) {
    const url = queue.shift()!;
    active++;
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    img.decode().catch(() => {}).finally(() => { active--; pump(); });
  }
}
const thumbsOf = (posts: Post[]) => posts.flatMap((p) => {
  const main = p.photos.find((x) => x.kind === 'STRIP') ?? p.photos[0];
  return [main, ...p.photos.filter((x) => x !== main).slice(0, 3)].map((x) => resolveApiUrl(x?.thumbUrl)).filter((u): u is string => !!u);
});

// ------------------------------------------------------------------ screen

export function renderFeed(root: HTMLElement, me: User) {
  const content = shell(root, { title: 'Bảng tin', tab: '#/feed', brand: true,
    actions: [h('a.icon', { href: '#/memories', title: 'Kỷ niệm', 'aria-label': 'Kỷ niệm' }, '🗓')] });
  const list = h('div.feed', { 'aria-busy': 'true' });
  const extras = h('div.feedextras');
  const status = h('div.feedend');
  content.append(pushCard(), extras, list, status);

  let cursor: string | null = null;
  let done = false;
  let loading: Promise<void> | null = null;
  let gen = 0; // bumps on reset so stale responses are dropped
  let count = 0;
  const seen = new Set<string>();

  const io = new IntersectionObserver((entries) => {
    if (entries.some((e) => e.isIntersecting)) void loadMore();
    // the huge top margin makes a sentinel we jumped past (fast fling, scrollIntoView) still count
  }, { rootMargin: '100000px 0px 1200px 0px' });

  /** The sentinel sits AHEAD pages before the end: reaching it loads the next page. */
  const placeSentinel = () => {
    io.disconnect();
    if (done) return;
    const cards = list.querySelectorAll('.post');
    const target = cards[Math.max(0, cards.length - AHEAD * PAGE)] ?? status;
    io.observe(target);
  };

  const append = (posts: Post[]) => {
    for (const p of posts) {
      if (seen.has(p.id)) continue;
      seen.add(p.id);
      const card = postCard(p, me, { eager: count < 2, onDeleted: () => card.remove() });
      count++;
      list.append(card);
    }
  };

  const paintEnd = () => {
    if (!count) {
      status.replaceChildren(empty('🌷', 'Chụp cùng bạn bè rồi đăng lên đây nhé.',
        h('a.primary.btn', { href: '#/rooms' }, '🎞 Rủ bạn chụp'), 'Bảng tin còn trống'));
    } else status.replaceChildren(done ? h('p.tiny', {}, 'Bạn đã xem hết rồi ♡') : skeleton('post', 1));
  };

  const loadMore = (): Promise<void> => {
    if (loading || done) return loading ?? Promise.resolve();
    const g = gen;
    loading = (async () => {
      try {
        const page: FeedPage = await api.feedPage(cursor, PAGE);
        if (g !== gen) return;
        if (cursor === null) { // fresh first page: replace whatever the cache painted
          list.replaceChildren();
          seen.clear();
          count = 0;
          cache.set(me.id, page.items);
          setCounts({ feed: 0 }); // seen the newest posts: clear the dot
        }
        append(page.items);
        cursor = page.nextCursor;
        done = !cursor;
        list.removeAttribute('aria-busy');
        paintEnd();
        // decode the pages beyond the first screen before they scroll in
        predecode(thumbsOf(page.items.slice(count <= PAGE ? 2 : 0)));
      } catch (e) {
        if (g !== gen) return;
        if (!count) list.replaceChildren(h('div.error', {}, h('p', {}, (e as Error).message), h('button.secondary', { onclick: () => reset() }, 'Thử lại')));
        else status.replaceChildren(h('button.secondary.more', { onclick: () => void loadMore() }, 'Thử lại'));
        done = false;
        loading = null;
        return;
      }
      loading = null;
      placeSentinel(); // may fire again right away until AHEAD pages are buffered
    })();
    return loading;
  };

  const reset = () => {
    gen++;
    cursor = null;
    done = false;
    loading = null;
    const c = cache.get(me.id);
    if (c?.items.length) {
      // instant paint from the last session; replaced by the fresh page below
      list.replaceChildren();
      seen.clear();
      count = 0;
      append(c.items);
    } else list.replaceChildren(skeleton('post', 2));
    void loadMore();
    void loadExtras(extras, me);
  };

  reset();
  return () => io.disconnect();
}

/** At-risk streak reminders (max 3) + the "Ngày này…" memory card. */
async function loadExtras(box: HTMLElement, me: User) {
  const [streaks, otd] = await Promise.all([
    api.streaks().catch(() => [] as Streak[]),
    api.onThisDay().catch(() => null),
  ]);
  box.replaceChildren(
    ...streaks.filter((s) => s.atRisk).slice(0, 3).map(streakCard),
    ...(otd?.sessions?.length ? [onThisDayCard(otd, me)] : []));
}

function streakCard(s: Streak) {
  return h('aside.streakcard', {},
    h('span.sc-fire', { 'aria-hidden': 'true' }, '🔥', h('b', {}, String(s.days))),
    h('div.sc-text', {},
      h('b', {}, `${s.days} ngày với ${s.user.displayName}`),
      h('small', {}, 'Chụp chung hôm nay để giữ chuỗi')),
    h('button.primary.small', { 'aria-label': `Rủ ${s.user.displayName} chụp chung`, onclick: () => void inviteToShoot(s.user) }, '🎞 Rủ chụp'));
}

function onThisDayCard(d: OnThisDay, me: User) {
  const date = new Date(`${d.date}T12:00:00`);
  const thumbs = d.sessions.slice(0, 3).map((s) => {
    const p = s.photos.find((x) => x.kind === 'SHOT') ?? s.photos[0];
    return p ? h('a.otd-thumb', { href: `#/memories?day=${d.date}`, 'aria-label': 'Xem lại buổi chụp' }, photoImg(p, 'pimg')) : null;
  }).filter((x): x is HTMLAnchorElement => !!x);
  const people = new Map<string, User>();
  for (const s of d.sessions) for (const p of s.participants) if (p.user.id !== me.id) people.set(p.user.id, p.user);
  return h('aside.otd', {},
    h('div.otd-head', {},
      h('div', {},
        h('p.otd-kicker', {}, 'Ngày này · ', d.label),
        h('h3', {}, date.toLocaleDateString('vi-VN', { day: 'numeric', month: 'long', year: 'numeric' }))),
      people.size ? h('span.avs', {}, ...[...people.values()].slice(0, 3).map((u) => avatar(u, 26))) : null),
    h('div.otd-thumbs', {}, ...thumbs),
    h('a.secondary.small.btn', { href: `#/memories?day=${d.date}` }, 'Xem lại ›'));
}
